import { cn } from '@jeraisonne/ui';
import type { BlogSocialLink } from '@jeraisonne/db';
import { AvatarCircle } from '@/components/avatar-circle';
import { SOCIAL_NETWORK_ACCOUNTS } from './brand-icons';
import type { BlogAuthor } from '@/lib/blog/blog-client';
import { BlogShareButton } from './blog-share-button';

/**
 * Icônes « Réagissez sur les réseaux » (LinkedIn / Facebook / Instagram), une
 * par réseau passé en `links`. Le lien pointe sur le POST réel de l'article
 * quand il existe (`link.postUrl`), sinon sur le compte officiel JeRaisonne.
 * Porté depuis apps/marketing/src/components/blog-author.tsx (avatar
 * remplacé par `AvatarCircle`, cohérent avec le reste du fil d'actu app).
 */
export function BlogSocialReactions({
  links,
  variant = 'card',
  label = 'Réagissez sur les réseaux',
  shareTitle,
  shareText,
}: {
  links: BlogSocialLink[];
  variant?: 'card' | 'overlay';
  label?: string;
  shareTitle?: string;
  shareText?: string;
}) {
  if (!links.length) return null;
  const overlay = variant === 'overlay';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span
        className={cn(
          'text-xs font-medium',
          overlay ? 'text-white drop-shadow-sm' : 'text-gray-500',
        )}
      >
        {label}
      </span>
      <ul className="flex items-center gap-1.5">
        {links.map(({ network, postUrl }) => {
          const { label: netLabel, href: account, Icon } = SOCIAL_NETWORK_ACCOUNTS[network];
          return (
            <li key={network}>
              <a
                href={postUrl ?? account}
                target="_blank"
                rel="noreferrer"
                aria-label={
                  postUrl ? `${netLabel} — voir la publication` : `${netLabel} — JeRaisonne`
                }
                className={cn(
                  'inline-flex items-center justify-center rounded-full transition-colors',
                  overlay
                    ? 'size-9 text-white backdrop-blur-sm hover:bg-black/60'
                    : 'hover:border-primary-300 hover:text-primary-600 size-10 border border-gray-200 bg-white text-gray-500',
                )}
              >
                <Icon className="size-5" />
              </a>
            </li>
          );
        })}
        {shareTitle && (
          <>
            <li
              aria-hidden
              className={cn('mx-0.5 h-5 w-px', overlay ? 'bg-white/30' : 'bg-gray-200')}
            />
            <li>
              <BlogShareButton
                title={shareTitle}
                text={shareText}
                variant={overlay ? 'overlay' : 'default'}
                shape="icon"
              />
            </li>
          </>
        )}
      </ul>
    </div>
  );
}

/**
 * Carte auteur en pied d'article : avatar + nom + libellé, et — quand
 * l'article a des publications réseaux associées — les icônes.
 */
export function BlogAuthorCard({ author }: { author: BlogAuthor }) {
  return (
    <section className="border-ink-200 mt-6 rounded-2xl border bg-white p-5 sm:p-6">
      <div className="flex items-center gap-4">
        <AvatarCircle name={author.display_name} src={author.avatar_url} size="lg" />
        <div>
          <p className="text-ink-400 text-[11px] font-bold uppercase tracking-wide">
            Auteur du sujet
          </p>
          <p className="text-ink-900 text-base font-bold">{author.display_name}</p>
        </div>
      </div>
    </section>
  );
}
